import type { ProfileSteps } from './ProfileStep'
import type { User } from '@/payload-types'

/**
 * Returns true if the given string is non-null and has content after trim.
 */
function hasValue(s: string | null | undefined): boolean {
  return typeof s === 'string' && s.trim().length > 0
}

type RichTextValue = User['profile'] extends { bio?: infer B } ? B : unknown

/**
 * Rich text is "filled" if it has at least one child node with content.
 */
function hasRichTextValue(value: RichTextValue): boolean {
  if (value == null || typeof value !== 'object') return false
  const root = (value as { root?: { children?: unknown[] } }).root
  const children = root?.children
  if (!Array.isArray(children) || children.length === 0) return false
  return children.some((child) => {
    if (child && typeof child === 'object' && 'text' in child) {
      const text = (child as { text?: string }).text
      return typeof text === 'string' && text.trim().length > 0
    }
    return true
  })
}

export type ProfileStepStatus = 'incomplete' | 'inProgress' | 'completed'

/**
 * Returns the status for a step: incomplete (no inputs), inProgress (some but not all, excluding email), or completed (all).
 * Use this to drive step icons (arrow / in-progress / check) in the guided intro.
 */
export function getProfileStepStatus(user: User | null, step: ProfileSteps): ProfileStepStatus {
  if (!user) return 'incomplete'

  switch (step) {
    case 'profile': {
      // Fields that count toward "in progress" (excluding email)
      const profileFields = [
        user.firstName,
        user.lastName,
        user.profile?.username,
        user.profile?.mobile,
        user.profile?.affiliation,
      ]
      const filled = profileFields.filter((v) => hasValue(v)).length
      const total = profileFields.length
      if (filled === 0) return 'incomplete'
      if (filled < total) return 'inProgress'
      // All profile fields (excl. email) + email required for completed
      if (!hasValue(user.email)) return 'inProgress'
      return 'completed'
    }

    case 'socials': {
      const links = user.socials?.socialLinks
      if (!links) return 'incomplete'
      const count = [
        links.facebook,
        links.x,
        links.instagram,
        links.linkedin,
        links.tiktok,
        links.youtube,
      ].filter((v) => hasValue(v)).length
      if (count === 0) return 'incomplete'
      if (count < 6) return 'inProgress'
      return 'completed'
    }

    case 'location': {
      const loc = user.location?.location
      if (!loc) return 'incomplete'
      const locationFields = [loc.address, loc.city, loc.state, loc.zip]
      const filled = locationFields.filter((v) => hasValue(v)).length
      if (filled === 0) return 'incomplete'
      if (filled < 4) return 'inProgress'
      return 'completed'
    }

    default:
      return 'incomplete'
  }
}

/**
 * Returns true if the user has values for all inputs covered by the given profile step.
 */
export function isProfileStepComplete(user: User | null, step: ProfileSteps): boolean {
  return getProfileStepStatus(user, step) === 'completed'
}

type UserCompletionResult = {
  total: number
  incompleteCount: number
  incompletePercentage: number
  completeCount: number
  completePercentage: number
}

/**
 * Evaluates every user-facing field on the User (excluding workOsId and introCompleted),
 * and returns the count and percentage of fields that are incomplete.
 * Used for profile completion progress (e.g. dashboard).
 */
export function getUserCompletion(user: User | null): UserCompletionResult {
  const total = 21 // fixed set of fields (subscriptions excluded; re-instate: 25 and uncomment checks below)
  if (!user) {
    return {
      total,
      incompleteCount: total,
      incompletePercentage: 100,
      completeCount: 0,
      completePercentage: 0,
    }
  }

  const checks: boolean[] = [
    // top-level (5)
    hasValue(user.email),
    hasValue(user.firstName),
    hasValue(user.lastName),
    user.role != null,
    user.image != null,
    // profile (4)
    hasValue(user.profile?.username),
    hasValue(user.profile?.mobile),
    hasValue(user.profile?.affiliation),
    hasRichTextValue(user.profile?.bio ?? null),
    // socials (6)
    hasValue(user.socials?.socialLinks?.facebook),
    hasValue(user.socials?.socialLinks?.x),
    hasValue(user.socials?.socialLinks?.instagram),
    hasValue(user.socials?.socialLinks?.linkedin),
    hasValue(user.socials?.socialLinks?.tiktok),
    hasValue(user.socials?.socialLinks?.youtube),
    // location (5)
    hasValue(user.location?.location?.address),
    hasValue(user.location?.location?.address2),
    hasValue(user.location?.location?.city),
    hasValue(user.location?.location?.state ?? undefined),
    hasValue(user.location?.location?.zip),
    // topics (1) – relationship
    Array.isArray(user.topics?.topics) && user.topics.topics.length > 0,
  ]

  const completeCount = checks.filter(Boolean).length
  const incompleteCount = total - completeCount
  const incompletePercentage = Math.round((incompleteCount / total) * 100)
  const completePercentage = Math.round((completeCount / total) * 100)
  return {
    total,
    incompleteCount,
    incompletePercentage,
    completeCount,
    completePercentage,
  }
}
